import { bindable, customElement, observable, resolve } from 'aurelia';
import { I18N } from '@aurelia/i18n';
import template from './media-owner-picker.html';
import { getTransferTargets } from '../services/api.service';
import type { AppUser } from '../services/api.types';

@customElement({ name: 'media-owner-picker', template })
export class MediaOwnerPicker {
  private readonly i18n: I18N = resolve(I18N);

  @bindable public selectedCount = 0;
  @bindable public confirmed!: (target: AppUser) => void;
  @bindable public closed!: () => void;
  @bindable public busy = false;

  public users: AppUser[] = [];
  @observable public searchText = '';
  public selectedUserId: number | null = null;
  public loading = false;
  public error = '';

  public get filteredUsers(): AppUser[] {
    const query = this.searchText.trim().toLocaleLowerCase();
    if (!query) return this.users;
    return this.users.filter(user =>
      user.username.toLocaleLowerCase().includes(query) ||
      (user.displayName?.toLocaleLowerCase().includes(query) ?? false) ||
      (user.email?.toLocaleLowerCase().includes(query) ?? false));
  }

  public async attached(): Promise<void> {
    this.loading = true;
    this.error = '';
    try {
      this.users = await getTransferTargets();
    } catch (e: unknown) {
      this.error = e instanceof Error ? e.message : this.i18n.tr('gallery.selection.transferOwnerLoadFailed');
    } finally {
      this.loading = false;
    }
  }

  public userLabel(user: AppUser): string {
    const displayName = user.displayName?.trim();
    return displayName && displayName !== user.username
      ? `${displayName} (${user.username})`
      : user.username;
  }

  public close(): void {
    if (!this.busy) this.closed();
  }

  public confirm(): void {
    if (this.busy || this.selectedUserId == null) return;
    const target = this.users.find(user => user.id === this.selectedUserId);
    if (target) this.confirmed(target);
  }
}
